import { Component, OnInit, OnChanges, Input, Inject } from 'angular-ts-decorators';
import { BasketEvent } from '@gn-audio/sfcc-basket/enums';
import { IBasketEventBus, ISFCCProductItem } from '@gn-audio/sfcc-basket/models';
import { getAvailabilityState } from '@gn-audio/core/api/pricing/utilities';
import { fetchSiteFeatures, SiteFeature, ISiteFeature } from '@gn-audio/core/api/site-settings';
import { getServiceForProduct} from '@gn-audio/sfcc-basket/utilities/get-service-for-product';
import { IProductWithAttributes } from '@gn-audio/core/api/product-catalogue/models/product-with-attributes';
import { IFromPrice, ISFCCGroupPrice } from '@gn-audio/core/api/pricing/models';
import { fetchGroupPrice } from '@gn-audio/core/api/pricing/fetch-group-price';
import { AvailabilityState } from '@gn-audio/core/common';
import { ServiceType } from '@gn-audio/core/api/product-catalogue/enums/service-type';
import { IProduct } from '@gn-audio/core/api/product-catalogue/models';

import template from './product-engraving-form.component.html';

declare var BasketEventBus: IBasketEventBus;

@Component({
  selector: 'productEngravingForm',
  template,
})
export class ProductEngravingFormComponent implements OnInit, OnChanges {
  public static $inject: ReadonlyArray<string> = [];

  public isEnabled = false;
  public isEngravingAllowed = false;
  public engravingMessage = '';

  @Input() public readonly group?: IProductWithAttributes;
  @Input('&') public readonly onEngravingMessageChange?: (args: { message: string; }) => void;

  // Have to use $scope in component to run $digests manually
  // because we integrate with application that is outside AngularJS runtime.
  constructor(@Inject('$scope') private readonly $scope: ng.IScope) { }

  public get hasEngravingMessage() {
    return !!this.engravingMessage;
  }

  public async ngOnInit() {
    await this.handleEngravingFeatureState();
    if (!this.isEnabled) {
      return;
    }

    // At that point Basket event bus might not be available,
    // so we will subscribe to corresponded event and then subscribe.
    if (typeof BasketEventBus === 'undefined') {
      window.addEventListener(BasketEvent.EventBusReady, this.subscribe);
    } else {
      this.subscribe();
    }
  }

  public async ngOnChanges() {
    await this.handleEngravingFeatureState();
    await this.handleChanges();
    this.$scope.$applyAsync();
  }

  private subscribe = () => {
    BasketEventBus.subscribe(BasketEvent.AddToBasket, this.clearEngraving);
  };

  private clearEngraving = (sku: string, message: string) => {
    // Empty engraving message when product is added to the basket.
    if (this.group.sku === sku && this.engravingMessage === message) {
      this.applyEngraving('');
    }
  };

  public showEngravingPopup() {
    if (typeof BasketEventBus === 'undefined') {
      return;
    }

    BasketEventBus.post(
      BasketEvent.ShowEngravingPopup,
      { c_engravingMsg: this.engravingMessage, product_id: this.group.sku } as ISFCCProductItem,
      this.applyEngraving,
    );
  }

  private async handleChanges() {
    if (!this.isEnabled) {
      return;
    }

    this.engravingMessage = '';
    if (!this.group) {
      this.isEngravingAllowed = false;
      return;
    }

    const groupPrice: IFromPrice | ISFCCGroupPrice | null = await fetchGroupPrice(this.group.groupId);
    const availabilityState: AvailabilityState = getAvailabilityState(groupPrice);
    const engravingService: IProduct = await getServiceForProduct(this.group.sku, ServiceType.Engraving)
    this.isEngravingAllowed = (!!engravingService && availabilityState <= AvailabilityState.BackOrder);
    this.applyEngraving(this.engravingMessage);
  }

  /**
   * Handles `SFCCBasketEngraving` feature toggle state for the component
   * @async
   */
  private async handleEngravingFeatureState(): Promise<void> {
    const features: ISiteFeature[] | null = await fetchSiteFeatures([SiteFeature.SfccBasketEngraving]);
    this.isEnabled = Array.isArray(features) && features.length > 0 && features[0].value;
  }

  private applyEngraving = (message: string) => {
    this.engravingMessage = message;
    if (this.onEngravingMessageChange) {
      this.onEngravingMessageChange({ message: this.engravingMessage });
    }

    this.$scope.$applyAsync();
  };
}
